/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import { CluRoutes } from 'src/clu/CluRoutes';
import { createCluProjectHandlerGroup } from 'src/clu/modules/handlers/projects/cluProjectHandlerGroup';
import { NotFoundRedirect } from 'src/core/components/NotFoundRoute';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useInstallHandlerGroup } from 'src/core/hooks/useInstallHandlerGroup';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { CustomTextRoutes } from 'src/customText/CustomTextRoutes';
import { DemoPage } from 'src/languageStudio/appShell/DemoPage';
import { ProjectsListPage } from 'src/languageStudio/appShell/landingPage/components/ProjectsListPage';
import { LanguageStudioLandingPage } from 'src/languageStudio/appShell/landingPage/LanguageStudioLandingPage';
import { StudioStartupConfig } from 'src/modules/startupConfigs/StudioStartupConfig';
import { createQnaMakerProjectHandlerGroup } from 'src/qnaMaker/modules/handlers/project/qnaMakerProjectHandlerGroup';
import { QnaMakerRoutes } from 'src/qnaMaker/QnaMakerRoutes';
import { TranslationRoutes } from 'src/translation/TranslationRoutes';

const theme = getScopedTheme('LanguageStudio');

const containerStyles = classNamesFunction<IStackProps, IStackStyles>()({ root: { color: `${theme.defaultTextColor} !important` } });

/**
 * The main routes component that creates the main routes for
 * the language studio portal.
 */
export const LanguageStudioRoutes = observer(() => {
    const { pathname } = useLocation();
    const { dispatcher, rootStore, uiStore } = useGlobalState();

    const { isSignedIn } = rootStore.authStore;

    useInstallHandlerGroup(dispatcher, createCluProjectHandlerGroup);
    useInstallHandlerGroup(dispatcher, createQnaMakerProjectHandlerGroup);

    const isDttEnabled = FeatureToggles.DocumentTranslationTool;

    const isLanguageStudio = uiStore.portal.kind === 'languageStudio';
    const isInLuis = doesRouteMatchLocation(routeTable.luis.root(), { locationToMatch: pathname });
    const isInRoot = doesRouteMatchLocation(routeTable.languageStudio.root(), { exact: true, locationToMatch: pathname });
    const isErrorPage =
        doesRouteMatchLocation(routeTable.notAuthorizedPage(), { exact: true, locationToMatch: pathname }) ||
        doesRouteMatchLocation(routeTable.notFoundPage(), { exact: true, locationToMatch: pathname });

    if (isSignedIn && isInRoot) {
        return <Redirect exact from={routeTable.languageStudio.root()} to={routeTable.languageStudio.home()} />;
    }

    if (isSignedIn && isLanguageStudio && isInLuis) {
        return <Redirect to={pathname.replace('/languageStudio/luis', '')} />;
    }

    return (
        <Stack id="languageStudioRoutesRoot" verticalFill styles={containerStyles}>
            <Switch>
                <Route path={routeTable.qnaMaker.root()} component={QnaMakerRoutes} />

                {(FeatureToggles.EnableCustomSkills || FeatureToggles.EnableCLUCustomSkills) && (
                    <Route path={[routeTable.clu.root(), routeTable.orchestration.root()]} component={CluRoutes} />
                )}
                {FeatureToggles.EnableCustomSkills && <Route path={routeTable.customText.root()} component={CustomTextRoutes} />}

                {(!isSignedIn || isDttEnabled) && <Route path={routeTable.translation.root()} component={TranslationRoutes} />}

                <Route path={routeTable.languageStudio.demos()} component={DemoPage} />

                <PrivateRoute exact path={routeTable.languageStudio.projects()} component={ProjectsListPage} />

                <Route exact path={routeTable.languageStudio.home()} component={LanguageStudioLandingPage} />

                {!isSignedIn && <Route exact path={routeTable.languageStudio.root()} component={LanguageStudioLandingPage} />}

                <NotFoundRedirect />
            </Switch>

            {isSignedIn && !isErrorPage && <StudioStartupConfig />}
        </Stack>
    );
});
